'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';

import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

type StoryStat = { number: string; labelKey: string };

type Story = {
  headlineKey: string;
  company: string;
  image: string;
  logo: string;
  logoWidth?: number;
  imageClassName?: string;
  stats: StoryStat[];
  link: string;
  quote?: string;
  author?: { name: string; title: string };
};

const AUTOPLAY_MS = 8000;

export default function Testimonials() {
  const { t, isRTL } = useTranslation();

  const ALL_TESTIMONIALS: Story[] = [
    {
      headlineKey: 'How MoneyGram supports 55M customers across 200+ countries',
      company: 'MoneyGram',
      image: '/images/homepage/testimonials/moneygram.png',
      logo: '/logos/logo-moneygram.svg',
      logoWidth: 180,
      imageClassName: 'object-center',
      stats: [
        { number: '70%', labelKey: 'automation' },
        { number: '55M', labelKey: 'customers' },
        { number: 'ATH', labelKey: 'CSAT' },
      ],
      link: '/customer-stories/moneygram',
      quote:
        'Open lets us deliver instant, multilingual answers to millions of customers, 24/7, at a consistency our team could never reach alone.',
    },
    {
      headlineKey: 'How Mollie supports 250,000+ businesses across Europe',
      company: 'Mollie',
      image:
        'https://framerusercontent.com/images/iiaJ36Mos7o2vmT6MiFDCIyOCs.png?width=1920&height=1080',
      logo: '/logos/logo-mollie.svg',
      logoWidth: 140,
      imageClassName: 'object-top',
      stats: [
        { number: '60%+', labelKey: 'automation' },
        { number: '250K+', labelKey: 'businesses' },
        { number: '24/7', labelKey: '8 languages' },
      ],
      link: '/customer-stories/mollie',
      quote:
        'With Open we automate the majority of merchant questions while keeping the human touch where it really matters.',
    },
    {
      headlineKey:
        'How TicketSwap automated 67% of 19 million user support operations',
      company: 'TicketSwap',
      image:
        'https://s3-eu-west-1.amazonaws.com/ticketswap-public/public/201903/a8775f68-ac30-47ea-bac7-2b5f4392efcc.jpeg',
      logo: '/logos/logo-ticketswap.svg',
      logoWidth: 160,
      imageClassName: 'object-center',
      stats: [
        { number: '67%', labelKey: 'automation' },
        { number: '19M', labelKey: 'users' },
        { number: 'ATH', labelKey: 'CSAT' },
      ],
      link: '/customer-stories/ticketswap',
      quote:
        'Open helped us reach an all-time high CSAT while taking pressure off our team during peak event windows.',
    },
    {
      headlineKey:
        'How Trunkrs automates 80% of delivery support with 4.8/5 rating',
      company: 'Trunkrs',
      image: 'https://trunkrs.com/hubfs/image%2021.svg',
      logo: '/logos/logo-trunkrs.svg',
      logoWidth: 150,
      imageClassName: 'object-cover',
      stats: [
        { number: '80%', labelKey: 'automation' },
        { number: '4.8/5', labelKey: 'rating' },
        { number: '60K+', labelKey: 'reviews' },
      ],
      link: '/customer-stories/trunkrs',
      quote:
        'We resolve the vast majority of delivery questions automatically and still keep a 4.8/5 customer rating.',
    },
    {
      headlineKey:
        'OTO is automating more than 77% of the support with +90% CSAT score',
      company: 'OTO',
      image:
        'https://laffaz.com/wp-content/uploads/2020/11/oto-mena-last-mile-delivery.jpg',
      logo: '/logos/logo-oto.png',
      logoWidth: 110,
      imageClassName: 'object-center',
      stats: [
        { number: '77%', labelKey: 'automation' },
        { number: '90%+', labelKey: 'CSAT' },
        { number: '24/7', labelKey: 'coverage' },
      ],
      link: '/customer-stories/oto',
      quote:
        'Open transformed our support: 77% of conversations are now resolved instantly while CSAT keeps climbing.',
    },
    {
      headlineKey:
        'More.com is automating +67% with all-time high customer satisfaction',
      company: 'More.com',
      image: 'https://www.more.com/image_hands_1',
      logo: '/logos/logo-morecom.svg',
      logoWidth: 160,
      imageClassName: 'object-cover',
      stats: [
        { number: '67%', labelKey: 'automation' },
        { number: '30M', labelKey: 'tickets' },
        { number: 'ATH', labelKey: 'CSAT' },
      ],
      link: '/customer-stories/more',
      quote:
        'With Open we scaled support across European markets without scaling headcount, and CSAT is at an all-time high.',
    },
    {
      headlineKey: 'How Qoyod automates 77% of support for 25,000+ businesses',
      company: 'Qoyod',
      image: '/images/homepage/testimonials/qoyod.png',
      logo: '/logos/logo-qoyod.png',
      logoWidth: 130,
      imageClassName: 'object-cover',
      stats: [
        { number: '77%', labelKey: 'automation' },
        { number: '25K+', labelKey: 'businesses' },
        { number: '4', labelKey: 'channels' },
      ],
      link: '/customer-stories/qoyod',
      quote:
        'Open answers the majority of accounting questions across every channel, so our customers get help the moment they ask.',
    },
    {
      headlineKey:
        'How OCN automates time-sensitive car subscriptions across Mexico?',
      company: 'OneCar Now',
      image: '/images/homepage/testimonials/ocn.png',
      logo: '/logos/logo-ocn.png',
      logoWidth: 110,
      imageClassName: 'object-cover',
      stats: [
        { number: '60%', labelKey: 'automation' },
        { number: '7.5K', labelKey: 'vehicles' },
        { number: '90min', labelKey: 'avg response' },
      ],
      link: '/customer-stories/ocn',
      quote:
        "Open lets us deliver same-day responses on time-sensitive subscriptions, something we couldn't do manually.",
    },
  ];

  /* Arabic-site customers, matched on `company` rather than by index so
     reordering ALL_TESTIMONIALS cannot silently put one back. OTO is not
     here on purpose: it is the one MENA customer the English site still
     carries, on the wall and in this carousel. */
  const AR_ONLY = new Set(['Qoyod']);

  const TESTIMONIALS: Story[] = isRTL
    ? [
        ALL_TESTIMONIALS[6], // Qoyod
        ALL_TESTIMONIALS[4], // OTO
        ALL_TESTIMONIALS[0], // MoneyGram
        ALL_TESTIMONIALS[1], // Mollie
        ALL_TESTIMONIALS[2], // TicketSwap
        ALL_TESTIMONIALS[3], // Trunkrs
        ALL_TESTIMONIALS[5], // More.com
        ALL_TESTIMONIALS[7], // OneCar Now
      ]
    : ALL_TESTIMONIALS.filter((story) => !AR_ONLY.has(story.company));

  // Server renders index 0 for stable markup, client picks a random
  // starting slide on mount to avoid hydration mismatches.
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const hasRandomized = useRef(false);

  useEffect(() => {
    if (hasRandomized.current) return;
    hasRandomized.current = true;
    if (TESTIMONIALS.length > 1) {
      setIndex(Math.floor(Math.random() * TESTIMONIALS.length));
    }
  }, [TESTIMONIALS.length]);

  const goTo = useCallback(
    (next: number) => {
      const total = TESTIMONIALS.length;
      setIndex(((next % total) + total) % total);
    },
    [TESTIMONIALS.length],
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (isHovered || TESTIMONIALS.length < 2) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % TESTIMONIALS.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [isHovered, TESTIMONIALS.length]);

  const story = TESTIMONIALS[index];

  // Section hidden site-wide per request. Placed after all hooks (Rules of
  // Hooks satisfied); the `boolean` type keeps the JSX below reachable for
  // the type-checker while returning null at runtime. Set to false to restore
  // the success-stories carousel on every page that renders <Testimonials />.
  const HIDDEN: boolean = true;
  if (HIDDEN) return null;

  return (
    <section className="bg-black py-16 text-white md:py-32 lg:py-40">
      <div className="container">
        <SubtleEntrance className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="!ml-0 !pl-0">
            <p className="mb-4 text-xs font-medium tracking-widest text-white/40 uppercase">
              Success Stories
            </p>
            <h2 className="font-serif text-2xl text-white sm:text-3xl md:text-4xl">
              {t.testimonials['Impacting the lives']}{' '}
              {t.testimonials['of millions of users.']}
            </h2>
          </div>
          <div className="flex flex-col items-start gap-5 md:max-w-md md:items-end md:ltr:text-right md:rtl:text-left">
            <p className="text-base leading-relaxed text-white/50">
              {
                t.testimonials[
                  'Companies using Open to automate customer communication at scale. 70%+ automation rates. All-time high satisfaction scores. 24/7 coverage in multiple languages.'
                ]
              }
            </p>
          </div>
        </SubtleEntrance>

        <SubtleEntrance delay={0.15} className="mt-14 md:mt-20">
          <div
            className={cn(
              'group/slider relative isolate w-full overflow-hidden rounded-3xl',
              'border border-white/10 bg-[#0a0a0a]',
              '[contain:paint] [transform:translateZ(0)]',
              'transition-colors duration-200 hover:border-white/25',
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocusCapture={() => setIsHovered(true)}
            onBlurCapture={() => setIsHovered(false)}
          >
            {/* Stretched link: makes the entire card a click target for the
                case study. Interactive controls inside (prev/next buttons,
                the visible "Read story" link) bump themselves above this
                overlay with `relative z-10` and remain individually
                clickable. */}
            <Link
              href={story.link}
              prefetch
              aria-label={`Read ${story.company} customer story`}
              className="absolute inset-0 z-[1] rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            />
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
              {/* ───────────────────────── Content (left) ───────────────────────── */}
              <div className="relative flex flex-col justify-between gap-8 p-8 sm:p-10 md:h-[600px] md:p-10 lg:h-[640px] lg:p-14">
                {/* Soft brand glow behind content */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-32 -z-10 h-72 w-72 rounded-full bg-white/[0.04] blur-3xl ltr:-left-32 rtl:-right-32"
                />

                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={`${story.link}-logo`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.4 }}
                    className="flex h-9 items-center sm:h-10"
                  >
                    <Image
                      src={story.logo}
                      alt={story.company}
                      width={story.logoWidth ?? 140}
                      height={36}
                      unoptimized
                      className="h-7 w-auto max-w-[180px] object-contain object-left opacity-90 [filter:brightness(0)_invert(1)] sm:h-8"
                    />
                  </motion.div>
                </AnimatePresence>

                <div className="flex flex-1 flex-col justify-center min-h-[180px] sm:min-h-[200px] md:min-h-0">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={`${story.link}-quote`}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <blockquote
                        className={cn(
                          'font-sans font-normal text-white',
                          'text-[22px] leading-[1.25] sm:text-[26px] md:text-[30px] lg:text-[32px]',
                        )}
                      >
                        <span className="text-white/30">“</span>
                        {story.quote ??
                          t.testimonials[
                            story.headlineKey as keyof typeof t.testimonials
                          ]}
                        <span className="text-white/30">”</span>
                      </blockquote>

                      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/55">
                        <div className="flex items-center gap-3">
                          <span className="h-px w-8 bg-white/20" />
                          {story.author ? (
                            <span>
                              <span className="text-white">
                                {story.author.name}
                              </span>
                              <span className="text-white/40"> · </span>
                              {story.author.title}
                              <span className="text-white/40"> · </span>
                              {story.company}
                            </span>
                          ) : (
                            <span>{story.company}</span>
                          )}
                        </div>
                        <Link
                          href={story.link}
                          prefetch
                          aria-label={`Read ${story.company} customer story`}
                          tabIndex={-1}
                          className="group/cta relative z-10 inline-flex items-center gap-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white"
                        >
                          Read story
                          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/cta:-translate-y-0.5 group-hover/cta:translate-x-0.5" />
                        </Link>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Stats row */}
                <AnimatePresence mode="wait" initial={false}>
                  <motion.dl
                    key={`${story.link}-stats`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.45, delay: 0.05 }}
                    className="grid grid-cols-3 gap-3 border-t border-white/10 pt-6 sm:gap-6"
                  >
                    {story.stats.map((stat) => (
                      <div key={stat.labelKey} className="min-w-0">
                        <dt className="text-[10px] uppercase tracking-[0.16em] text-white/45">
                          {t.testimonials[
                            stat.labelKey as keyof typeof t.testimonials
                          ] ?? stat.labelKey}
                        </dt>
                        <dd className="mt-1.5 font-sans font-normal text-2xl leading-none text-white tabular-nums sm:text-3xl md:text-[34px]">
                          {stat.number}
                        </dd>
                      </div>
                    ))}
                  </motion.dl>
                </AnimatePresence>

                {/* Slider controls */}
                <div className="flex items-center justify-between gap-6">
                  <div className="flex items-center gap-4 text-xs tabular-nums text-white/45">
                    <span className="font-medium text-white">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div
                      className="relative h-px w-20 overflow-hidden bg-white/15 sm:w-28"
                      role="progressbar"
                      aria-label="Testimonial carousel progress"
                      aria-valuemin={1}
                      aria-valuemax={TESTIMONIALS.length}
                      aria-valuenow={index + 1}
                    >
                      <span
                        className="absolute inset-y-0 bg-white transition-[width] duration-500 ease-out ltr:left-0 rtl:right-0"
                        style={{
                          width: `${((index + 1) / TESTIMONIALS.length) * 100}%`,
                        }}
                      />
                    </div>
                    <span>{String(TESTIMONIALS.length).padStart(2, '0')}</span>
                  </div>

                  <div className="relative z-10 flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={isRTL ? next : prev}
                      aria-label="Previous story"
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-full',
                        'border border-white/15 bg-white/[0.04] text-white/80',
                        'transition-all duration-200 hover:border-white/40 hover:bg-white hover:text-neutral-900',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                      )}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={isRTL ? prev : next}
                      aria-label="Next story"
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-full',
                        'border border-white/15 bg-white/[0.04] text-white/80',
                        'transition-all duration-200 hover:border-white/40 hover:bg-white hover:text-neutral-900',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                      )}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* ───────────────────────── Image (right) ───────────────────────── */}
              <div className="relative h-72 overflow-hidden sm:h-96 md:h-auto">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={`${story.link}-img`}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.01 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                  >
                    <Image
                      src={story.image}
                      alt={story.company}
                      fill
                      unoptimized
                      sizes="(min-width: 1024px) 55vw, 100vw"
                      className={cn(
                        'object-cover',
                        story.imageClassName ?? 'object-center',
                      )}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Seamless blend into the left content panel */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 md:hidden"
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(10,10,10,0) 60%, rgba(10,10,10,0.85) 100%)',
                  }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 hidden md:block"
                  style={{
                    background:
                      'linear-gradient(to right, rgba(10,10,10,1) 0%, rgba(10,10,10,0.95) 12%, rgba(10,10,10,0.7) 28%, rgba(10,10,10,0.35) 45%, rgba(10,10,10,0) 70%)',
                  }}
                />
              </div>
            </div>
          </div>
        </SubtleEntrance>
      </div>
    </section>
  );
}
